import type { FC, ReactNode, RefObject } from 'react'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { motion, useAnimationFrame, useMotionValue } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { TransitionLink } from '@/components/generals/page-transition'
import { workItems } from '@/constants/work'

interface MarqueeRowProps {
    images: string[]
    direction: 1 | -1
    speed: number
    slowRef: RefObject<boolean>
}

/**
 * One row of images drifting sideways. The speed eases down while the
 * pointer rests on the section and eases back up when it leaves.
 */
const MarqueeRow: FC<MarqueeRowProps> = ({ images, direction, speed, slowRef }): ReactNode => {
    const trackRef = useRef<HTMLDivElement>(null)
    const x = useMotionValue(0)
    const half = useRef(0)
    const current = useRef(speed)
    const reduced = useRef(false)

    useEffect(() => {
        reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const track = trackRef.current

        if (!track) return

        const measure = () => {
            half.current = track.scrollWidth / 3
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(track)

        return () => observer.disconnect()
    }, [])

    useAnimationFrame((_, delta) => {
        if (reduced.current || !half.current) return

        const target = slowRef.current ? speed * 0.18 : speed

        current.current += (target - current.current) * Math.min(1, delta / 260)

        let next = x.get() + direction * current.current * (delta / 1000)

        if (next <= -half.current) next += half.current
        if (next > 0) next -= half.current

        x.set(next)
    })

    return (
        <motion.div ref={trackRef} className='flex w-max' style={{ x }}>
            {[...images, ...images, ...images].map((src, index) => (
                <div
                    key={`${src}-${index}`}
                    className='relative mr-2.5 aspect-[15/14] w-[170px] shrink-0 overflow-hidden rounded-[6px] bg-surface md:w-[300px]'
                >
                    <Image src={src} alt='' fill sizes='(min-width: 768px) 300px, 170px' className='object-cover' />
                </div>
            ))}
        </motion.div>
    )
}

export const ExploreBand: FC = (): ReactNode => {
    const slowRef = useRef(false)
    const images = workItems.slice(0, 15).map(item => item.cover.src)
    const topRow = images.slice(0, 5)
    const middleRow = images.slice(5, 10)
    const bottomRow = images.slice(10)

    return (
        <section
            id='explore'
            aria-labelledby='explore-title'
            className='relative overflow-hidden py-6'
            onMouseEnter={() => {
                slowRef.current = true
            }}
            onMouseLeave={() => {
                slowRef.current = false
            }}
        >
            <div aria-hidden='true' className='flex flex-col gap-2.5'>
                <MarqueeRow images={topRow} direction={-1} speed={42} slowRef={slowRef} />
                <MarqueeRow images={middleRow} direction={1} speed={36} slowRef={slowRef} />
                <MarqueeRow images={bottomRow} direction={-1} speed={30} slowRef={slowRef} />
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-black/45 bg-[radial-gradient(ellipse_70%_80%_at_60%_50%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.75)_100%)]' />
            <div aria-hidden='true' className='pointer-events-none absolute inset-y-0 left-0 w-[60vw] bg-linear-to-r from-background/95 via-background/60 to-transparent' />

            <div className='absolute inset-0 flex flex-col items-start justify-center px-4 md:px-[30px]'>
                <h2 id='explore-title' className='type-display-xl max-w-[10ch] text-foreground'>
                    See everything I have made
                </h2>

                <TransitionLink href='/work' className='group mt-10 inline-flex items-center gap-3 text-sm font-medium text-foreground'>
                    <ArrowRight className='size-4 text-gold transition-transform duration-500 group-hover:translate-x-1' aria-hidden='true' />
                    <span className='underline decoration-white/30 underline-offset-[6px] group-hover:text-gold'>Browse all work</span>
                </TransitionLink>
            </div>
        </section>
    )
}
